<template>
	<div class="content">
		<section class="page-header">
			<div class="page-header-inner">
				<div class="page-kicker">Open Computational Catalysis Database</div>
				<h1 class="page-title">
					{{ data.textDescription1 }}
				</h1>
				<p class="page-desc">
					{{ data.textDescription2 }}
				</p>
				<p class="page-subdesc">
					{{ data.textDescription3 }}
				</p>
			</div>
		</section>

		<section class="section">
			<div class="section-head">
				<h2 class="section-title">WHAT'S INSIDE</h2>
			</div>

			<div class="inside-card">
				<div class="inside-left">
					<ul class="inside-list">
						<li v-for="(line, idx) in whatInsideLines" :key="idx">
							{{ line }}
						</li>
					</ul>
				</div>

				<div class="inside-divider"></div>

				<div class="inside-right">
					<div v-for="(item, idx) in whatInside2MainMetrics" :key="idx" class="metric-row">
						<span class="metric-label">{{ item.label }}</span>
						<span v-if="item.value" class="metric-value">{{ item.value }}</span>
					</div>
					<div v-if="whatInside2LastLine" class="metric-updated">
						<span class="metric-updated-date">{{ whatInside2LastLine }}</span>
					</div>
				</div>
			</div>
		</section>

		<section class="section" v-if="data.ys_img && data.ys_img.value">
			<div class="image-panel">
				<img
					class="coverage-image"
					:src="data.ys_img.value"
					alt="Coverage"
				/>
			</div>
		</section>

		<section class="section">
			<div class="section-head">
				<h2 class="section-title">DATA OVERVIEW</h2>
			</div>

			<div class="info-grid three-col">
				<div class="info-panel">
					<h3 class="panel-title">Catalysts</h3>
					<ul class="panel-list">
						<li v-for="(line, idx) in catalystsLines" :key="idx">
							{{ line }}
						</li>
					</ul>
				</div>

				<div class="info-panel">
					<h3 class="panel-title">Reactions</h3>
					<ul class="panel-list">
						<li v-for="(line, idx) in reactionsLines" :key="idx">
							{{ line }}
						</li>
					</ul>
				</div>

				<div class="info-panel">
					<h3 class="panel-title">Descriptors</h3>
					<ul class="panel-list">
						<li v-for="(line, idx) in descriptorsLines" :key="idx">
							{{ line }}
						</li>
					</ul>
				</div>
			</div>
		</section>

		<section class="section tutorial-section">
			<div class="section-head">
				<h2 class="section-title">HOW TO USE Open-ADC</h2>
			</div>

			<div class="tutorial-stack">
				<div class="tutorial-row">
					<div class="tutorial-text">
						<h3 class="tutorial-title">Structure-visualization gallery</h3>
						<p class="tutorial-desc">
							The structure-visualization gallery displays the atomic configuration of each catalyst entry and supports inspection of different adsorption states, including oxygen-adsorbed structures.
						</p>
					</div>
					<div class="tutorial-image">
						<img src="@/assets/tutorial-structure-gallery.png" alt="Structure visualization gallery" />
					</div>
				</div>

				<div class="tutorial-row">
					<div class="tutorial-text">
						<h3 class="tutorial-title">Search materials across multiple properties</h3>
						<p class="tutorial-desc">
							Open-ADC enables rapid screening across intrinsic structure properties, oxygen-adsorption properties, predicted catalytic performance, synthetic methodology, and associated references and literature.
						</p>
					</div>
					<div class="tutorial-image">
						<img src="@/assets/tutorial-search-properties.png" alt="Search materials across properties" />
					</div>
				</div>

				<div class="tutorial-row">
					<div class="tutorial-text">
						<h3 class="tutorial-title">Computed XANES for the center metal</h3>
						<p class="tutorial-desc">
							Open-ADC provides computed XANES for the center metal of each material. The spectra are generated using FEFF10, enabling direct comparison between simulated and experimental spectral features.
						</p>
					</div>
					<div class="tutorial-image">
						<img src="@/assets/tutorial-xanes-feff10.png" alt="Computed XANES by FEFF10" />
					</div>
				</div>
			</div>
		</section>

		<section class="section">
			<div class="section-head">
				<h2 class="section-title">WHY DIFFERENT</h2>
			</div>

			<div class="why-panel">
				<ul class="why-list">
					<li v-for="(line, idx) in whyDifferentLines" :key="idx">
						{{ line }}
					</li>
				</ul>
			</div>
		</section>

		<section class="section quick-entry-section">
			<div class="section-head">
				<h2 class="section-title">QUICK ENTRY</h2>
			</div>

			<div class="quick-entry-minimal">
				<div class="quick-entry-center">
					<router-link class="quick-entry-link" to="/PeriodicTable">
						Search by Element
					</router-link>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	name: 'pageIndex',
	computed: {
		splitLines() {
			return (raw) => {
				if (!raw) return []
				return String(raw)
					.split(/\r?\n/)
					.map((s) => s.trim())
					.filter(Boolean)
			}
		},
		whatInside2Lines() {
			return this.splitLines(this.data && this.data.what_inside2)
		},
		whatInside2MainLines() {
			const lines = this.whatInside2Lines
			if (!lines.length) return []
			return lines.slice(0, -1)
		},
		whatInside2MainMetrics() {
			return this.whatInside2MainLines.map((line) => {
				const parts = String(line).trim().split(/\s+/).filter(Boolean)
				if (parts.length <= 1) {
					return { label: parts[0] || '', value: '' }
				}
				const value = parts[parts.length - 1]
				const label = parts.slice(0, -1).join(' ')
				return { label, value }
			})
		},
		whatInside2LastLine() {
			const lines = this.whatInside2Lines
			return lines.length ? lines[lines.length - 1] : ''
		},
		catalystsLines() {
			return this.splitLines(this.data && this.data.catalysts)
		},
		reactionsLines() {
			return this.splitLines(this.data && this.data.reactions)
		},
		descriptorsLines() {
			return this.splitLines(this.data && this.data.descriptors)
		},
		whatInsideLines() {
			return this.splitLines(this.data && this.data.what_inside)
		},
		whyDifferentLines() {
			return this.splitLines(this.data && this.data.why_different_content)
		}
	},
	data() {
		return {
			value: 5,
			url: 'https://img.js.design/assets/img/6554e231e739773bfaa88af4.png#14b3acbe8aebca786af3bd16cfa3f15f',
			currentIndex: 0,
			visibleCount: 4,
			marginValue: 20,
			memberList: [],
			reverse: true,
			activities: [],
			paperList: [],
			paperTotal: 0,
			data: {},
			datalistTotal: null
		}
	},
	methods: {
		getData() {
			this.$api.news.getConfig().then((res) => {
				this.data = res.data.data
				localStorage.setItem('record_number', this.data.record_number.value)
				let userLanguage = localStorage.getItem('userLanguage')
				if (userLanguage == 'zh') {
					this.data.title = this.data.web_name.value
					this.data.webdesc = this.data.webdesc.value
					this.data.textDescription1 = this.data.textDescription1.value
					this.data.textDescription2 = this.data.textDescription2.value
					this.data.textDescription3 = this.data.textDescription3.value
					this.data.why_different_content = this.data.why_different_content.value
					this.data.catalysts = this.data.catalysts.value
					this.data.reactions = this.data.reactions.value
					this.data.descriptors = this.data.descriptors.value
					this.data.what_inside = this.data.what_inside.value
					this.data.what_inside2 = this.data.what_inside2.value
					this.data.index_bottom = this.data.index_bottom.value
				} else {
					this.data.title = this.data.web_name_en.value
					this.data.webdesc = this.data.webdesc_en.value
					this.data.textDescription1 = this.data.textDescription1_en.value
					this.data.textDescription2 = this.data.textDescription2_en.value
					this.data.textDescription3 = this.data.textDescription3_en.value
					this.data.why_different_content = this.data.why_different_content_en.value
					this.data.catalysts = this.data.catalysts_en.value
					this.data.reactions = this.data.reactions_en.value
					this.data.descriptors = this.data.descriptors_en.value
					this.data.what_inside = this.data.what_inside_en.value
					this.data.what_inside2 = this.data.what_inside2_en.value
					this.data.index_bottom = this.data.index_bottom_en.value
				}
			})
		},
		getDatalistTotal() {
			this.$api.news.getDatalistTotal().then((res) => {
				const total = res && res.data && res.data.data && res.data.data.total
				this.datalistTotal = typeof total === 'number' ? total : null
			}).catch(() => {
				this.datalistTotal = null
			})
		},
		formatNumber(value) {
			if (value === null || value === undefined) {
				return ''
			}
			const num = Number(value)
			if (Number.isNaN(num)) {
				return value
			}
			return num.toLocaleString()
		}
	},
	created() {
		this.getData()
		this.getDatalistTotal()
		this.show = true
	},
	mounted() {
		if (this.datalistTotal === null) {
			this.getDatalistTotal()
		}
	}
}
</script>

<style scoped>
.content {
	min-height: 100vh;
	padding: 24px 0 48px;
	box-sizing: border-box;
	background: #eef2f6;
	font-family: 'Open Sans Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
	color: #334155;
}

.quick-entry-section {
	padding-top: 4px;
}

.quick-entry-minimal {
	width: 100%;
	margin: 0 auto;
	padding-top: 14px;
	border-top: 1px solid #d9e2ec;
	align-items: center;
}

.quick-entry-center {
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
}

.quick-entry-link {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	padding: 8px 18px;
	color: #1f4fa3;
	font-weight: 600;
	font-size: 16px;
	text-decoration: none;
	border: 1px solid #d9e2ec;
	border-radius: 6px;
	background: #ffffff;
	transition: all 0.2s ease;
}

.quick-entry-link:hover {
	color: #ffffff;
	background: #1f4fa3;
	border-color: #1f4fa3;
	transform: translateY(-1px);
}
.page-header,
.section {
	max-width: 82%;
	margin: 0 auto;
}

.page-header {
	padding: 18px 0 26px;
	border-bottom: 1px solid #dde5ee;
	margin-bottom: 28px;
}

.page-header-inner {
	max-width: 1100px;
}

.page-kicker {
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #6b7280;
	margin-bottom: 10px;
}

.page-title {
	margin: 0 0 12px;
	font-size: 34px;
	font-weight: 700;
	line-height: 1.2;
	color: #1f4fa3;
}

.page-desc {
	margin: 0 0 8px;
	max-width: 980px;
	font-size: 19px;
	line-height: 1.8;
	color: #475569;
}

.page-subdesc {
	margin: 0;
	max-width: 980px;
	font-size: 18px;
	line-height: 1.8;
	color: #64748b;
}

.section {
	margin-bottom: 28px;
}

.section-head {
	margin-bottom: 14px;
}

.section-title {
	margin: 0;
	font-size: 24px;
	font-weight: 700;
	line-height: 1.25;
	color: #1f4fa3;
	text-align: center;
	letter-spacing: 0.02em;
}

.inside-card {
	display: flex;
	justify-content: space-between;
	align-items: stretch;
	background: #ffffff;
	border: 1px solid #e3e8ef;
	border-radius: 10px;
	padding: 20px 24px;
	box-sizing: border-box;
}

.inside-left {
	flex: 1;
	display: flex;
	align-items: center;
	padding-right: 22px;
}

.inside-list {
	list-style: disc;
	padding-left: 20px;
	margin: 0;
	font-size: 16px;
	color: #475569;
	line-height: 1.9;
}

.inside-divider {
	width: 1px;
	background-color: #d9e2ec;
	margin: 0 16px;
}

.inside-right {
	flex: 1;
	padding-left: 24px;
	display: flex;
	align-items: flex-start;
	flex-direction: column;
}

.metric-row {
	display: flex;
	justify-content: flex-start;
	align-items: baseline;
	font-size: 17px;
	color: #475569;
	margin-bottom: 10px;
	line-height: 1.8;
}

.metric-label {
	font-weight: 500;
}

.metric-value {
	font-weight: 700;
	color: #1f4fa3;
	font-size: 17px;
	margin-left: 16px;
}

.metric-updated {
	margin-top: 12px;
	font-size: 15px;
	color: #64748b;
	line-height: 1.8;
}

.metric-updated-date {
	margin-left: 4px;
}

.image-panel {
	width: 100%;
	background: #ffffff;
	border: 1px solid #e3e8ef;
	border-radius: 10px;
	padding: 16px;
	box-sizing: border-box;
}

.coverage-image {
	display: block;
	max-width: 80%;
	max-height: 400px;
	width: auto;
	height: auto;
	object-fit: contain;
	margin: 0 auto;
}

.info-grid {
	display: grid;
	gap: 18px;
}

.three-col {
	grid-template-columns: repeat(3, minmax(0, 1fr));
}

.info-panel {
	background: transparent;
	border: none;
	border-radius: 0;
	padding: 0 18px;
}

.three-col .info-panel {
	border-right: 1px solid #d9e2ec;
}

.three-col .info-panel:last-child {
	border-right: none;
}

.panel-title {
	margin: 0 0 12px;
	font-size: 18px;
	font-weight: 700;
	color: #1f4fa3;
}

.panel-text {
	margin: 0 0 12px;
	font-size: 16px;
	line-height: 1.85;
	color: #475569;
}

.panel-text:last-child {
	margin-bottom: 0;
}

.panel-list {
	margin: 0;
	padding-left: 20px;
	font-size: 16px;
	line-height: 1.9;
	color: #475569;
}

.tutorial-section {
	padding-top: 4px;
}

.tutorial-stack {
	display: flex;
	flex-direction: column;
	gap: 0;
}

.tutorial-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 28px;
	padding: 30px 0;
	border-top: 1px solid #d9e2ec;
}

.tutorial-row:first-child {
	border-top: none;
	padding-top: 8px;
}

.tutorial-text {
	flex: 1;
	min-width: 0;
	max-width: 980px;
}

.tutorial-title {
	margin: 0 0 14px;
	font-size: 22px;
	font-weight: 700;
	line-height: 1.35;
	color: #2f3a4a;
}

.tutorial-desc {
	margin: 0;
	font-size: 16px;
	line-height: 1.9;
	color: #5b6778;
}

.tutorial-image {
	flex: 0 0 32%;
	display: flex;
	justify-content: center;
}

.tutorial-image img {
	width: 100%;
	max-width: 100%;
	display: block;
	border: 1px solid #e3e8ef;
	border-radius: 6px;
	background: #ffffff;
}

.why-panel {
	background: #ffffff;
	border: 1px solid #e3e8ef;
	border-radius: 10px;
	padding: 18px 20px;
}

.why-list {
	list-style: none;
	padding: 0 0 0 20px;
	margin: 0;
	font-size: 16px;
	color: #475569;
	line-height: 1.9;
	border-left: 3px solid #1f4fa3;
}

.why-list li {
	position: relative;
	padding-left: 12px;
	margin-bottom: 6px;
}

.why-list li:last-child {
	margin-bottom: 0;
}

.quick-entry-section {
	padding-top: 4px;
}


@media (max-width: 1100px) {
	.three-col {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 960px) {
	.page-header,
	.section {
		max-width: calc(100% - 28px);
	}

	.page-title {
		font-size: 28px;
	}

	.page-desc,
	.page-subdesc,
	.inside-list,
	.panel-list,
	.panel-text,
	.why-list,
	.quick-entry-minimal,
	.metric-row,
	.metric-updated,
	.tutorial-desc {
		font-size: 15px;
	}

	.inside-card {
		flex-direction: column;
	}

	.inside-left {
		padding-right: 0;
		margin-bottom: 16px;
	}

	.inside-divider {
		display: none;
	}

	.inside-right {
		padding-left: 0;
		margin-top: 4px;
	}

	.tutorial-row {
		flex-direction: column;
		align-items: stretch;
		gap: 18px;
		padding: 24px 0;
	}

	.tutorial-text {
		max-width: none;
	}

	.tutorial-title {
		font-size: 20px;
	}

	.tutorial-image {
		flex: none;
		width: 100%;
	}
}
</style>